import { useEffect, useMemo, useState } from 'react';
import { notify } from '../../../app';
import {
  getAgentPrompts,
  createAgentPrompt,
  updateAgentPrompt,
  deleteAgentPrompt,
} from '../../../api/agent-prompts';
import { useFetch, useRequest } from '../../../hooks';
import { useModels } from '../../../hooks/useModels';
import type { AgentPromptDto } from '../../../api/agent-prompts';

export type PromptsFormType = {
  end_of_conversation_metadata_extractor_prompt?: string;
  end_of_conversation_metadata_extractor_prompt_modelName?: string | null;
  end_of_conversation_metadata_extractor_prompt_modelSettings?: string;
  conversation_metrics_prompt?: string;
  conversation_metrics_prompt_modelName?: string | null;
  conversation_metrics_prompt_modelSettings?: string;
  pii_masking_prompt?: string;
  pii_masking_prompt_modelName?: string | null;
  pii_masking_prompt_modelSettings?: string;
  system_prompt?: string;
  system_prompt_modelName?: string | null;
  system_prompt_modelSettings?: string;
  inprompt_knowledge_base?: string;
  inprompt_knowledge_base_modelName?: string | null;
  inprompt_knowledge_base_modelSettings?: string;
  pdf_preprocessor_prompt?: string;
  pdf_preprocessor_prompt_modelName?: string | null;
  pdf_preprocessor_prompt_modelSettings?: string;
  agent_output_validator_prompt?: string;
  agent_output_validator_prompt_modelName?: string | null;
  agent_output_validator_prompt_modelSettings?: string;
};

const defaultFormValues: PromptsFormType = {
  end_of_conversation_metadata_extractor_prompt: '',
  end_of_conversation_metadata_extractor_prompt_modelName: null,
  end_of_conversation_metadata_extractor_prompt_modelSettings: '',
  conversation_metrics_prompt: '',
  conversation_metrics_prompt_modelName: null,
  conversation_metrics_prompt_modelSettings: '',
  pii_masking_prompt: '',
  pii_masking_prompt_modelName: null,
  pii_masking_prompt_modelSettings: '',
  system_prompt: '',
  system_prompt_modelName: null,
  system_prompt_modelSettings: '',
  inprompt_knowledge_base: '',
  inprompt_knowledge_base_modelName: null,
  inprompt_knowledge_base_modelSettings: '',
  pdf_preprocessor_prompt: '',
  pdf_preprocessor_prompt_modelName: null,
  pdf_preprocessor_prompt_modelSettings: '',
  agent_output_validator_prompt: '',
  agent_output_validator_prompt_modelName: null,
  agent_output_validator_prompt_modelSettings: '',
};

export const useAgentPrompts = (agentId: string) => {
  const {
    intializing: loadingPrompts,
    data: prompts,
    refresh: refreshPrompts,
  } = useFetch(() => getAgentPrompts(agentId), [agentId]);

  const { llmModels } = useModels();

  const loading = loadingPrompts;

  const promptsByName = useMemo(() => {
    const map = new Map<string, AgentPromptDto>();
    prompts?.forEach(prompt => {
      map.set(prompt.name, prompt);
    });
    return map;
  }, [prompts]);

  const initialFormValues = useMemo((): PromptsFormType => {
    if (!prompts || prompts.length === 0) {
      return defaultFormValues;
    }

    const getPrompt = (name: string) => promptsByName.get(name);

    return {
      end_of_conversation_metadata_extractor_prompt:
        getPrompt('end_of_conversation_metadata_extractor_prompt')?.prompt || '',
      end_of_conversation_metadata_extractor_prompt_modelName:
        getPrompt('end_of_conversation_metadata_extractor_prompt')?.modelName ?? null,
      end_of_conversation_metadata_extractor_prompt_modelSettings:
        getPrompt('end_of_conversation_metadata_extractor_prompt')?.modelSettings
          ? JSON.stringify(getPrompt('end_of_conversation_metadata_extractor_prompt')?.modelSettings, null, 2)
          : '',
      conversation_metrics_prompt:
        getPrompt('conversation_metrics_prompt')?.prompt || '',
      conversation_metrics_prompt_modelName:
        getPrompt('conversation_metrics_prompt')?.modelName ?? null,
      conversation_metrics_prompt_modelSettings:
        getPrompt('conversation_metrics_prompt')?.modelSettings
          ? JSON.stringify(getPrompt('conversation_metrics_prompt')?.modelSettings, null, 2)
          : '',
      pii_masking_prompt: getPrompt('pii_masking_prompt')?.prompt || '',
      pii_masking_prompt_modelName:
        getPrompt('pii_masking_prompt')?.modelName ?? null,
      pii_masking_prompt_modelSettings:
        getPrompt('pii_masking_prompt')?.modelSettings
          ? JSON.stringify(getPrompt('pii_masking_prompt')?.modelSettings, null, 2)
          : '',
      system_prompt: getPrompt('system_prompt')?.prompt || '',
      system_prompt_modelName: getPrompt('system_prompt')?.modelName ?? null,
      system_prompt_modelSettings: getPrompt('system_prompt')?.modelSettings
        ? JSON.stringify(getPrompt('system_prompt')?.modelSettings, null, 2)
        : '',
      inprompt_knowledge_base:
        getPrompt('inprompt_knowledge_base')?.prompt || '',
      inprompt_knowledge_base_modelName:
        getPrompt('inprompt_knowledge_base')?.modelName ?? null,
      inprompt_knowledge_base_modelSettings:
        getPrompt('inprompt_knowledge_base')?.modelSettings
          ? JSON.stringify(getPrompt('inprompt_knowledge_base')?.modelSettings, null, 2)
          : '',
      pdf_preprocessor_prompt:
        getPrompt('pdf_preprocessor_prompt')?.prompt || '',
      pdf_preprocessor_prompt_modelName:
        getPrompt('pdf_preprocessor_prompt')?.modelName ?? null,
      pdf_preprocessor_prompt_modelSettings:
        getPrompt('pdf_preprocessor_prompt')?.modelSettings
          ? JSON.stringify(getPrompt('pdf_preprocessor_prompt')?.modelSettings, null, 2)
          : '',
      agent_output_validator_prompt:
        getPrompt('agent_output_validator_prompt')?.prompt || '',
      agent_output_validator_prompt_modelName:
        getPrompt('agent_output_validator_prompt')?.modelName ?? null,
      agent_output_validator_prompt_modelSettings:
        getPrompt('agent_output_validator_prompt')?.modelSettings
          ? JSON.stringify(getPrompt('agent_output_validator_prompt')?.modelSettings, null, 2)
          : '',
    };
  }, [promptsByName, prompts]);

  const [formValues, setFormValues] =
    useState<PromptsFormType>(initialFormValues);
  const [formKey, setFormKey] = useState(0);

  const [savePrompt, savingPrompt] = useRequest(
    async (
      _signal,
      promptName: string,
      promptValue: string,
      modelName: string | null,
      rawModelSettings: string,
    ): Promise<'saved' | 'deleted' | 'noop'> => {
      const existingPrompt = prompts?.find(p => p.name === promptName);
      const trimmedPrompt = promptValue.trim();
      const parsedModelSettings = rawModelSettings
        ? JSON.parse(rawModelSettings)
        : null;

      if (!trimmedPrompt) {
        if (existingPrompt?.id) {
          await deleteAgentPrompt(agentId, existingPrompt.id);
          return 'deleted';
        }
        return 'noop';
      }

      if (existingPrompt?.id) {
        await updateAgentPrompt(agentId, existingPrompt.id, {
          prompt: trimmedPrompt,
          modelName,
          modelSettings: parsedModelSettings,
        });
      } else {
        await createAgentPrompt(agentId, {
          name: promptName,
          prompt: trimmedPrompt,
          modelName,
          modelSettings: parsedModelSettings,
        });
      }
      return 'saved';
    },
    {
      deps: [agentId, prompts, formValues],
      onSuccess: action => {
        if (action === 'deleted') {
          notify.success('Prompt deleted successfully');
        } else if (action === 'saved') {
          notify.success('Prompt saved successfully');
        }
        refreshPrompts?.();
      },
      onError: () => {
        notify.error('Failed to save prompt');
        refreshPrompts?.();
      },
    }
  );

  useEffect(() => {
    setFormValues(initialFormValues);
    setFormKey(prev => prev + 1);
  }, [initialFormValues]);

  const updating = savingPrompt;

  return {
    loading,
    updating,
    promptsByName,
    initialFormValues,
    formValues,
    formKey,
    setFormValues,
    savePrompt,
    llmModels,
  };
};
